import styles from '../templates/styles.css?inline';

// PARSE FORM TEMPLATES
function parseOptions(options) {
  if (!options) return [];
  if (Array.isArray(options)) return options;
  if (typeof options === "string") {
    try {
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      if (options.includes(',')) {
        return options.split(',').map(s => s.trim());
      }
      return [options];
    }
  }
  return [];
}

export class FormLoader {
  constructor(sdk, hostElement, config) {
    this.sdk = sdk;
    this.host = hostElement;
    this.config = config || {};

    // Retrieve form ID programmatically or from attribute
    this.formId = this.config.formId || this.host.getAttribute('data-logistics-form') || '7296d141-988a-4994-b248-06f4971f15be';

    // Configuration properties matching the Next.js API parameters
    this.createdBy = this.config.createdBy || "ShipDeez User";
    this.submissionSource = this.config.submissionSource || "WEB";
    this.defaultStatusTypeID = this.config.defaultStatusTypeID !== undefined ? this.config.defaultStatusTypeID : 1;
    this.requireCaptcha = this.config.requireCaptcha || false;
    this.onSubmitted = this.config.onSubmitted || null;

    this.fields = [];
    this.valuesByFieldId = {};
    this.loading = true;
    this.submitting = false;
    this.error = '';
    this.successId = '';

    this.shadowRoot = this.host.attachShadow({ mode: 'open' });
    this.handleSubmit = this.handleSubmit.bind(this);

    this.init();
  }

  async init() {
    // Append styles
    const styleEl = document.createElement('style');
    styleEl.textContent = styles;
    this.shadowRoot.appendChild(styleEl);

    // Create wrapper container
    this.container = document.createElement('div');
    this.container.style.width = '100%';
    this.container.style.boxSizing = 'border-box';
    this.shadowRoot.appendChild(this.container);

    this.render();

    await this.loadSchema();
  }

  async loadSchema() {
    this.loading = true;
    this.error = '';
    this.successId = '';
    this.fields = [];
    this.valuesByFieldId = {};
    this.render();

    try {
      let response;
      const apiUrl = 'https://dev-api.shipdeez.com/api/v1/Forms/fetchTemplate';

      // Attempt POST first
      try {
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ formTemplateId: this.formId, includeField: true })
        });
      } catch (err) {
        console.warn("POST fetchTemplate failed, trying GET fallback:", err);
      }

      // Fallback to GET if POST failed or returned error status
      if (!response || !response.ok) {
        const getUrl = `${apiUrl}?formTemplateId=${encodeURIComponent(this.formId)}&includeField=true`;
        response = await fetch(getUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (!response.ok) {
        throw new Error(`Server returned status: ${response.status}`);
      }

      const resJson = await response.json();

      // Handle wrapped API responses (responseCode and data object)
      const dataPayload = resJson.data || resJson;
      const resultObj = dataPayload.result || dataPayload;

      if (resJson.responseCode !== undefined && resJson.responseCode !== 200 && resJson.responseCode !== 201) {
        throw new Error(resJson.errorMessages?.[0]?.errorText || "Failed to load form schema.");
      }

      const apiFields = resultObj.fields || [];

      // Sort fields by DisplayOrder
      this.fields = [...apiFields].sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0));

      // Initialize values
      this.fields.forEach(f => {
        const type = (f.FieldTypeCode || "").toUpperCase();
        if (type === "BOOLEAN" || type === "CHECKBOX") {
          this.valuesByFieldId[f.FormFieldId] = false;
        } else {
          this.valuesByFieldId[f.FormFieldId] = "";
        }
      });

      this.loading = false;
    } catch (err) {
      console.error("Failed to fetch template schema:", err);
      this.error = err.message || "Failed to load form schema.";
      this.loading = false;
    }

    this.render();
  }

  setFieldValue(formFieldId, value) {
    this.valuesByFieldId[formFieldId] = value;
  }

  buildValuePayload(field, rawValue) {
    const type = (field.FieldTypeCode || "").toUpperCase();
    const trimmed = typeof rawValue === "string" ? rawValue.trim() : "";

    if (type === "NUMBER") {
      const parsed = trimmed === "" ? null : Number(trimmed);
      return {
        formFieldId: field.FormFieldId,
        valueText: null,
        valueNumber: Number.isFinite(parsed) ? parsed : null,
        valueDate: null,
        valueBit: null,
        valueFileRef: null,
      };
    }

    if (type === "DATE") {
      return {
        formFieldId: field.FormFieldId,
        valueText: null,
        valueNumber: null,
        valueDate: trimmed || null,
        valueBit: null,
        valueFileRef: null,
      };
    }

    if (type === "BOOLEAN" || type === "CHECKBOX") {
      return {
        formFieldId: field.FormFieldId,
        valueText: null,
        valueNumber: null,
        valueDate: null,
        valueBit: trimmed === "true" || rawValue === true,
        valueFileRef: null,
      };
    }

    return {
      formFieldId: field.FormFieldId,
      valueText: trimmed || null,
      valueNumber: null,
      valueDate: null,
      valueBit: null,
      valueFileRef: null,
    };
  }

  async handleSubmit(e) {
    e.preventDefault();
    this.error = "";
    this.renderStatusBox();

    // Client-side validations
    for (const field of this.fields) {
      const type = (field.FieldTypeCode || "").toUpperCase();
      const rawValue = this.valuesByFieldId[field.FormFieldId];
      if (type === "BOOLEAN" || type === "CHECKBOX") {
        if (field.IsRequired && !rawValue) {
          this.error = `${field.FieldLabel} is required.`;
          this.renderStatusBox();
          return;
        }
      } else {
        const value = String(rawValue || "").trim();
        if (field.IsRequired && !value) {
          this.error = `${field.FieldLabel} is required.`;
          this.renderStatusBox();
          return;
        }
      }
    }

    const values = this.fields.map(field =>
      this.buildValuePayload(field, this.valuesByFieldId[field.FormFieldId] || "")
    );

    const payload = {
      formTemplateId: this.formId,
      statusTypeID: this.defaultStatusTypeID,
      siteId: null,
      eventId: null,
      submittedByUserId: null,
      submittedByEmail: null,
      submissionSource: this.submissionSource,
      emailMessageId: null,
      ipAddress: "127.0.0.1",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "WEB",
      createdBy: this.createdBy,
      values,
    };

    this.submitting = true;
    this.updateSubmitButtonState();

    try {
      const response = await fetch('https://dev-api.shipdeez.com/api/v1/Forms/submission', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Submission failed with status: ${response.status}`);
      }

      const resJson = await response.json();
      const dataPayload = resJson.data || resJson;
      const resultObj = dataPayload.result || dataPayload;

      if (resJson.responseCode !== undefined && resJson.responseCode !== 200 && resJson.responseCode !== 201) {
        throw new Error(resJson.errorMessages?.[0]?.errorText || "Form submission failed.");
      }

      const formSubmissionId = resultObj.formSubmissionId || "";
      this.successId = formSubmissionId;

      if (this.onSubmitted) {
        this.onSubmitted(formSubmissionId);
      }

      // Reset values
      this.fields.forEach(f => {
        this.valuesByFieldId[f.FormFieldId] = "";
      });
    } catch (err) {
      console.error("Failed to submit dynamic form:", err);
      this.error = err.message || "Form submission failed.";
    } finally {
      this.submitting = false;
      this.render();
    }
  }

  updateSubmitButtonState() {
    const btn = this.shadowRoot.getElementById('btn-dynamic-submit');
    if (btn) {
      btn.disabled = this.submitting;
      btn.innerText = this.submitting ? "Submitting..." : "Submit";
    }
  }

  renderStatusBox() {
    const errorBox = this.shadowRoot.getElementById('dynamic-form-error-box');
    if (errorBox) {
      if (this.error) {
        errorBox.style.display = 'block';
        errorBox.innerText = this.error;
      } else {
        errorBox.style.display = 'none';
      }
    }
  }

  render() {
    this.container.innerHTML = '';

    if (this.loading) {
      const loaderDiv = document.createElement('div');
      loaderDiv.style.textAlign = 'center';
      loaderDiv.style.padding = '40px 0';
      loaderDiv.style.color = '#64748b';
      loaderDiv.style.fontSize = '14px';

      const spinner = document.createElement('div');
      spinner.style.border = '3px solid #f3f3f3';
      spinner.style.borderTop = '3px solid #f97316';
      spinner.style.borderRadius = '50%';
      spinner.style.width = '24px';
      spinner.style.height = '24px';
      spinner.style.animation = 'spin 1s linear infinite';
      spinner.style.margin = '0 auto 12px auto';
      loaderDiv.appendChild(spinner);

      const spiningStyle = document.createElement('style');
      spiningStyle.textContent = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
      this.shadowRoot.appendChild(spiningStyle);

      const text = document.createElement('span');
      text.innerText = "Loading custom form config...";
      loaderDiv.appendChild(text);

      this.container.appendChild(loaderDiv);
      return;
    }

    if (this.error && this.fields.length === 0) {
      const errorDiv = document.createElement('div');
      errorDiv.style.color = '#ef4444';
      errorDiv.style.fontSize = '14px';
      errorDiv.style.padding = '16px';
      errorDiv.style.border = '1px solid #fca5a5';
      errorDiv.style.borderRadius = '12px';
      errorDiv.style.background = '#fef2f2';
      errorDiv.style.textAlign = 'center';
      errorDiv.innerText = `Error: ${this.error}`;
      this.container.appendChild(errorDiv);
      return;
    }

    if (this.successId) {
      const successDiv = document.createElement('div');
      successDiv.style.display = 'flex';
      successDiv.style.flexDirection = 'column';
      successDiv.style.alignItems = 'center';
      successDiv.style.justifyContent = 'center';
      successDiv.style.textAlign = 'center';
      successDiv.style.padding = '32px 16px';

      const checkCircle = document.createElement('div');
      checkCircle.style.width = '64px';
      checkCircle.style.height = '64px';
      checkCircle.style.background = 'rgba(16, 185, 129, 0.1)';
      checkCircle.style.borderRadius = '50%';
      checkCircle.style.display = 'flex';
      checkCircle.style.alignItems = 'center';
      checkCircle.style.justifyContent = 'center';
      checkCircle.style.marginBottom = '16px';
      checkCircle.innerHTML = `<svg viewBox="0 0 24 24" width="32" height="32" stroke="#10B981" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`;
      successDiv.appendChild(checkCircle);

      const title = document.createElement('h3');
      title.style.fontSize = '18px';
      title.style.fontWeight = '700';
      title.style.color = '#0f172a';
      title.style.marginBottom = '8px';
      title.innerText = "Form Submitted Successfully!";
      successDiv.appendChild(title);

      const subtitle = document.createElement('p');
      subtitle.style.fontSize = '14px';
      subtitle.style.color = '#64748b';
      subtitle.style.marginBottom = '24px';
      subtitle.innerText = "Thank you for your submission. Your response has been recorded.";
      successDiv.appendChild(subtitle);

      const resetBtn = document.createElement('button');
      resetBtn.style.width = '100%';
      resetBtn.style.padding = '12px 16px';
      resetBtn.style.borderRadius = '12px';
      resetBtn.style.border = '1px solid #e2e8f0';
      resetBtn.style.background = '#ffffff';
      resetBtn.style.color = '#334155';
      resetBtn.style.fontSize = '14px';
      resetBtn.style.fontWeight = '600';
      resetBtn.style.cursor = 'pointer';
      resetBtn.style.transition = 'background 0.15s ease';
      resetBtn.innerText = "Submit Another Response";
      resetBtn.addEventListener('click', () => {
        this.successId = '';
        this.error = '';
        this.render();
      });
      successDiv.appendChild(resetBtn);

      this.container.appendChild(successDiv);
      return;
    }

    if (this.fields.length === 0) {
      const emptyDiv = document.createElement('div');
      emptyDiv.style.textAlign = 'center';
      emptyDiv.style.padding = '40px 0';
      emptyDiv.style.color = '#64748b';
      emptyDiv.style.fontSize = '14px';
      emptyDiv.innerText = "No fields found for this template.";
      this.container.appendChild(emptyDiv);
      return;
    }

    // Form element
    const formNode = document.createElement('form');
    formNode.style.display = 'flex';
    formNode.style.flexDirection = 'column';
    formNode.style.gap = '16px';
    formNode.addEventListener('submit', this.handleSubmit);

    // Fields loop
    this.fields.forEach(field => {
      const type = (field.FieldTypeCode || "").toUpperCase();

      if (type === 'CHECKBOX') {
        const formGroup = document.createElement('div');
        formGroup.style.display = 'flex';
        formGroup.style.flexDirection = 'row';
        formGroup.style.alignItems = 'flex-start';
        formGroup.style.gap = '12px';
        formGroup.style.padding = '14px 16px';
        formGroup.style.background = '#faf6ef';
        formGroup.style.border = '1px solid #ece4d6';
        formGroup.style.borderRadius = '12px';
        formGroup.style.cursor = 'pointer';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.style.marginTop = '2px';
        checkbox.style.width = '18px';
        checkbox.style.height = '18px';
        checkbox.style.accentColor = '#f97316';
        checkbox.style.flex = '0 0 auto';
        checkbox.style.cursor = 'pointer';

        if (field.IsRequired) {
          checkbox.required = true;
        }

        // Restore values
        checkbox.checked = !!this.valuesByFieldId[field.FormFieldId];

        checkbox.addEventListener('change', (e) => {
          this.setFieldValue(field.FormFieldId, e.target.checked);
        });

        const label = document.createElement('label');
        label.style.fontSize = '12.5px';
        label.style.lineHeight = '1.55';
        label.style.color = '#6f6658';
        label.style.cursor = 'pointer';
        label.innerText = field.FieldLabel;

        if (field.IsRequired) {
          const star = document.createElement('span');
          star.style.color = '#ef4444';
          star.style.marginLeft = '4px';
          star.innerText = '*';
          label.appendChild(star);
        }

        // Clicking the container toggles the checkbox
        formGroup.addEventListener('click', (e) => {
          if (e.target !== checkbox && e.target !== label && !label.contains(e.target)) {
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change'));
          }
        });

        formGroup.appendChild(checkbox);
        formGroup.appendChild(label);
        formNode.appendChild(formGroup);
        return;
      }

      const formGroup = document.createElement('div');
      formGroup.style.display = 'flex';
      formGroup.style.flexDirection = 'column';
      formGroup.style.gap = '6px';

      const label = document.createElement('label');
      label.style.fontSize = '12px';
      label.style.fontWeight = '600';
      label.style.color = '#475569';
      label.innerText = field.FieldLabel;
      if (field.IsRequired) {
        const star = document.createElement('span');
        star.style.color = '#ef4444';
        star.style.marginLeft = '4px';
        star.innerText = '*';
        label.appendChild(star);
      }
      formGroup.appendChild(label);

      let input;
      const commonStyle = "width: 100%; rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-gray-300 focus:ring-2 focus:ring-orange-100 transition duration-150 box-sizing: border-box;";

      if (type === 'TEXTAREA') {
        input = document.createElement('textarea');
        input.style.minHeight = '100px';
        input.style.resize = 'vertical';
      } else if (type === 'DROPDOWN') {
        input = document.createElement('select');

        const defaultOpt = document.createElement('option');
        defaultOpt.value = "";
        defaultOpt.innerText = `Select ${field.FieldLabel}`;
        input.appendChild(defaultOpt);

        const options = parseOptions(field.OptionsJson);
        options.forEach(opt => {
          const optEl = document.createElement('option');
          optEl.value = opt;
          optEl.innerText = opt;
          input.appendChild(optEl);
        });
      } else {
        input = document.createElement('input');
        if (type === 'DATE') {
          input.type = 'date';
        } else if (type === 'NUMBER') {
          input.type = 'number';
        } else if (type === 'EMAIL') {
          input.type = 'email';
        } else {
          input.type = 'text';
        }
      }

      // Input config & styling
      input.className = 'input-control-bar';
      input.placeholder = field.Placeholder || '';
      if (field.IsRequired) {
        input.required = true;
      }

      // Restore values from state (to avoid losing input on any updates)
      input.value = this.valuesByFieldId[field.FormFieldId] || '';

      // Update state dynamically without full re-render on typing
      input.addEventListener('input', (e) => {
        this.setFieldValue(field.FormFieldId, e.target.value);
      });

      formGroup.appendChild(input);
      formNode.appendChild(formGroup);
    });

    // Error box placeholder
    const errorBox = document.createElement('div');
    errorBox.id = 'dynamic-form-error-box';
    errorBox.style.color = '#ef4444';
    errorBox.style.fontSize = '12px';
    errorBox.style.padding = '10px 12px';
    errorBox.style.border = '1px solid #fca5a5';
    errorBox.style.borderRadius = '12px';
    errorBox.style.background = '#fef2f2';
    errorBox.style.display = 'none';
    formNode.appendChild(errorBox);

    // Submit button
    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.id = 'btn-dynamic-submit';
    submitBtn.style.width = '100%';
    submitBtn.style.padding = '12px 16px';
    submitBtn.style.borderRadius = '12px';
    submitBtn.style.border = 'none';
    submitBtn.style.background = '#f97316';
    submitBtn.style.color = '#ffffff';
    submitBtn.style.fontSize = '14px';
    submitBtn.style.fontWeight = '700';
    submitBtn.style.cursor = 'pointer';
    submitBtn.style.transition = 'background 0.15s ease';
    submitBtn.innerText = "Submit";
    submitBtn.addEventListener('mouseenter', () => {
      if (!this.submitting) submitBtn.style.background = '#ea580c';
    });
    submitBtn.addEventListener('mouseleave', () => {
      if (!this.submitting) submitBtn.style.background = '#f97316';
    });

    formNode.appendChild(submitBtn);
    this.container.appendChild(formNode);
  }
}
